// Demo rows for the niche model (verticals, cities, areas, check texts) — the preview build runs
// Админ › Ниши / Градове / Нова ниша against these when Supabase is not configured.

import type { CheckTextRow, VerticalRow } from '../features/admin/verticals/useVerticals.js';
import type { CityAreaRow, CityRow } from '../features/admin/cities/useCities.js';
import type { QueryRow } from '../features/admin/useAdmin.js';

const query = (id: number, query_text: string, extra: Partial<QueryRow> = {}): QueryRow => ({
  id,
  query_text,
  vertical_key: 'dentist',
  city_key: 'burgas',
  area_hint: null,
  cooldown_days: 14,
  cooldown_jitter_days: 1,
  last_run_at: '2026-09-08T03:00:00Z',
  total_found: 0,
  new_found: 0,
  active: true,
  batch_id: null,
  approved_at: '2026-06-01T00:00:00Z',
  generated_by: 'seed',
  tier: 'core',
  runs: 3,
  cumulative_new: 0,
  zero_streak: 0,
  skipped_filter: 0,
  ...extra,
});

const DEMO_BATCH = '7f3c2a10-5b1e-4c7d-9e2f-0a1b2c3d4e5f';

export const MOCK_QUERIES: QueryRow[] = [
  query(1, 'зъболекар Бургас', { total_found: 84, new_found: 3, cumulative_new: 61 }),
  query(2, 'дентална клиника Бургас Лазур', {
    area_hint: 'Лазур',
    tier: 'core_area',
    cooldown_days: 21,
    total_found: 18,
    new_found: 0,
    cumulative_new: 4,
    zero_streak: 2,
  }),
  query(3, 'салон за красота Бургас', {
    vertical_key: 'beauty_salon',
    active: false,
    approved_at: null,
    generated_by: 'matrix',
    batch_id: DEMO_BATCH,
    last_run_at: null,
    runs: 0,
  }),
  query(4, 'маникюр Бургас', {
    vertical_key: 'beauty_salon',
    active: false,
    approved_at: null,
    generated_by: 'matrix',
    batch_id: DEMO_BATCH,
    tier: 'service',
    cooldown_days: 21,
    last_run_at: null,
    runs: 0,
  }),
];

const IMPORTANCE_DENTIST: Record<string, string> = {
  ssl: 'required',
  mobile_viewport: 'required',
  booking: 'required',
  prices: 'required',
  viber: 'required',
  contact_form: 'optional',
  team_page: 'optional',
  lang_en: 'optional',
  broken_links: 'ignore',
};

export const MOCK_VERTICALS: VerticalRow[] = [
  {
    key: 'dentist',
    name_bg: 'Зъболекари',
    name_en: 'Dentists',
    active: true,
    origin: 'seed',
    audience_noun_bg: 'пациенти',
    pitch_style_bg: 'Формално Вие, директно, без излишни думи.',
    places_included_types: ['dental_clinic', 'dentist'],
    name_include_kw: ['дентал', 'зъб', 'стомато', 'dental', 'dent'],
    name_exclude_kw: ['ветеринар', 'veterinar'],
    synonyms_bg: ['зъболекар', 'стоматолог', 'дентална клиника', 'дентален център'],
    synonyms_lat: ['zabolekar', 'stomatolog', 'dentist', 'dental clinic'],
    services_bg: ['импланти', 'ортодонт', 'брекети', 'избелване на зъби', 'детски зъболекар'],
    services_lat: ['implanti', 'ortodont', 'breketi'],
    search_languages: ['bg', 'lat'],
    intent_phrasings_bg: ['{syn} {city} цени', 'най-добър {syn} {city}', '{syn} {city} отзиви'],
    osm_tags: ['amenity=dentist'],
    booking_hints: ['superdoc', 'healee'],
    grid_sweep: true,
    discovery_share: 1,
    check_importance: IMPORTANCE_DENTIST,
    discovery_questions: [
      {
        when: 'no_website',
        text_bg: 'Как Ви намират пациентите сега — само през Google и препоръки?',
      },
      {
        when: 'check:booking',
        text_bg: 'Колко часа седмично отиват за записване на часове по телефона?',
      },
      { when: 'always', text_bg: 'Кой решава за сайта — Вие или някой друг?' },
    ],
    call_dead_hours: { lunch: '12:00–13:00' },
  },
  {
    key: 'beauty_salon',
    name_bg: 'Салони за красота',
    name_en: 'Beauty salons',
    active: false,
    origin: 'preset',
    audience_noun_bg: 'клиенти',
    pitch_style_bg: 'Приятелски, на „Вие“, без професионален жаргон.',
    places_included_types: ['beauty_salon', 'hair_salon', 'nail_salon', 'barber_shop', 'spa'],
    name_include_kw: ['салон', 'красота', 'козмет', 'фризьор', 'маникюр', 'beauty', 'salon'],
    name_exclude_kw: ['магазин', 'аптека', 'училище', 'дентал', 'хотел'],
    synonyms_bg: ['салон за красота', 'козметичен салон', 'фризьорски салон', 'студио за маникюр'],
    synonyms_lat: ['salon za krasota', 'kozmetichen salon', 'frizyorski salon', 'manikur'],
    services_bg: ['маникюр', 'педикюр', 'гел лак', 'ламиниране на мигли', 'лазерна епилация'],
    services_lat: ['manikur', 'pedikur', 'gel lak', 'laminirane na migli'],
    search_languages: ['bg', 'lat'],
    intent_phrasings_bg: ['{syn} {city} цени', 'най-добър {syn} {city}', '{syn} {city} отзиви'],
    osm_tags: ['shop=beauty', 'shop=hairdresser', 'leisure=spa'],
    booking_hints: ['booksy', 'fresha', 'treatwell'],
    grid_sweep: true,
    discovery_share: 1,
    check_importance: { ...IMPORTANCE_DENTIST, lang_en: 'optional', whatsapp: 'optional' },
    discovery_questions: [
      {
        when: 'always',
        text_bg: 'Как клиентите Ви записват час сега — телефон, Instagram, Viber?',
      },
      { when: 'check:booking', text_bg: 'Колко записвания губите вечер и в почивни дни?' },
    ],
    call_dead_hours: { friday_afternoon: 'петък след 14:00', saturday: 'събота' },
  },
];

export const MOCK_CITIES: CityRow[] = [
  {
    key: 'burgas',
    name_bg: 'Бургас',
    name_en: 'Burgas',
    name_lat: 'Burgas',
    slug: 'burgas',
    aliases: ['Бургас', 'гр. Бургас', 'Burgas'],
    center_lat: 42.5048,
    center_lng: 27.4626,
    radius_m: 8000,
    active: true,
  },
];

const area = (
  id: number,
  name: string,
  name_lat: string,
  extra: Partial<CityAreaRow> = {},
): CityAreaRow => ({
  id,
  city_key: 'burgas',
  name,
  name_lat,
  aliases: [name],
  include_in_queries: true,
  search_weight: 1,
  source: 'v1',
  has_polygon: false,
  ...extra,
});

export const MOCK_AREAS: CityAreaRow[] = [
  area(1, 'Център', 'Centar'),
  area(2, 'Лазур', 'Lazur'),
  area(3, 'Възраждане', 'Vazrazhdane'),
  area(4, 'Славейков', 'Slaveykov'),
  area(5, 'Меден рудник', 'Meden Rudnik', { search_weight: 2 }),
  area(6, 'Сарафово', 'Sarafovo'),
  area(7, 'Братя Миладинови', 'Bratya Miladinovi', { include_in_queries: false }),
];

export const MOCK_CHECK_TEXTS: CheckTextRow[] = [
  {
    id: 1,
    vertical_key: 'dentist',
    check_key: 'booking',
    title_bg: 'Онлайн запазване на час',
    ok_bg: 'Пациентите могат да запазят час онлайн.',
    missing_bg: 'Няма начин за запазване на час онлайн — всичко минава през телефон.',
    improvement_bg: 'Форма или календар за онлайн час, видим на всяка страница.',
    consequence_bg: 'Пациент с намерение за скъпо лечение няма как да запише час вечер.',
  },
  {
    id: 2,
    vertical_key: 'dentist',
    check_key: 'prices',
    title_bg: 'Цени',
    ok_bg: 'Сайтът показва цени или ценоразпис.',
    missing_bg: 'Няма никаква информация за цени.',
    improvement_bg: 'Ориентировъчен ценоразпис за най-търсените процедури.',
    consequence_bg: 'Рецепцията вдига обаждания само за цени — време, което не носи час.',
  },
];
